// frontend/src/farm/farmView.mjs
// The farm world is a Phaser Container the scene moves and scales (the camera stays the
// retina camera, so HUD, modals and toasts are untouched). This is the maths for that
// transform: view = { x, y, zoom } = container position (scene units) and scale.
// Pure (node --test).

export const ZOOM = { min: 0.5, max: 1.5, wheelStep: 0.1 };

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function createView(zoom = 1) { return { x: 0, y: 0, zoom }; }

export function screenToLocal(view, sx, sy) {
  return { x: (sx - view.x) / view.zoom, y: (sy - view.y) / view.zoom };
}

export function localToScreen(view, lx, ly) {
  return { x: view.x + lx * view.zoom, y: view.y + ly * view.zoom };
}

export function followView(view, target, viewport) {
  return { zoom: view.zoom, x: viewport.w / 2 - target.x * view.zoom, y: viewport.h / 2 - target.y * view.zoom };
}

export function panView(view, dx, dy) { return { ...view, x: view.x + dx, y: view.y + dy }; }

export function zoomAt(view, focus, newZoom) {
  const zoom = clamp(newZoom, ZOOM.min, ZOOM.max);
  const local = screenToLocal(view, focus.x, focus.y);
  return { zoom, x: focus.x - local.x * zoom, y: focus.y - local.y * zoom };
}

export function clampView(view, extent, viewport, margin = 0.25) {
  const w = extent.w * view.zoom, h = extent.h * view.zoom;
  const x = clamp(view.x, viewport.w * margin - w, viewport.w * (1 - margin));
  const y = clamp(view.y, viewport.h * margin - h, viewport.h * (1 - margin));
  return { ...view, x, y };
}

export function lerpView(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, zoom: b.zoom };
}

// ---- smooth zoom and gradual re-centring ---------------------------------------------
// The wheel moves a zoom TARGET multiplicatively (a notch is the same feel at any zoom, and a
// trackpad's small deltas give fine steps); the view then eases toward it every frame, the way
// League of Legends' camera does, instead of jumping 0.1 per notch.
const WHEEL_BASE = 1.0015;           // per wheel-delta unit: one 100-unit notch ≈ ×1.16
const ZOOM_TAU_MS = 110;
export function wheelZoomTarget(target, deltaY) {
  return clamp(target * Math.pow(WHEEL_BASE, -deltaY), ZOOM.min, ZOOM.max);
}

// Share of the remaining distance to cover in dtMs with time constant tauMs: exponential
// smoothing that looks the same at 30, 60 or 120 fps.
export function easeFactor(dtMs, tauMs) { return 1 - Math.exp(-Math.max(0, dtMs) / tauMs); }

export function stepZoom(view, target, focus, dtMs) {
  const z = Math.abs(target - view.zoom) < 1e-4 ? target : view.zoom + (target - view.zoom) * easeFactor(dtMs, ZOOM_TAU_MS);
  return zoomAt(view, focus, z);
}

// Following the avatar: tauMs while walking. After a manual pan the camera comes back
// gradually — it starts very lazy (recenterTauMs) and eases to the normal follow over
// recenterRampMs, so the view glides home instead of snapping.
export const FOLLOW = { tauMs: 125, recenterTauMs: 1100, recenterRampMs: 1800 };
export function followTau(sinceMs) {
  if (sinceMs >= FOLLOW.recenterRampMs) return FOLLOW.tauMs;
  const s = Math.max(0, sinceMs) / FOLLOW.recenterRampMs;
  const e = s * s * (3 - 2 * s);
  return FOLLOW.recenterTauMs + (FOLLOW.tauMs - FOLLOW.recenterTauMs) * e;
}

// One step of the follow-cam toward `goal`. Exponential easing never quite arrives, and the last
// sub-pixel creep made the backdrop shiver as pixel snapping flipped back and forth — so within
// a third of a pixel the view settles exactly on the goal.
export function approachView(view, goal, alpha, eps = 1 / 3) {
  const next = lerpView(view, goal, alpha);
  return Math.abs(goal.x - next.x) < eps && Math.abs(goal.y - next.y) < eps ? { ...goal } : next;
}
